import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import FloatingAIChat from '../../components/ui/FloatingAIChat';
import VideoPlayer from './components/VideoPlayer';
import VideoInfo from './components/VideoInfo';
import ChapterList from './components/ChapterList';
import RelatedVideos from './components/RelatedVideos';
import VideoNotes from './components/VideoNotes';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import MediaPlayer from '../../components/media/MediaPlayer';
import { useLesson, useAllLessons, useModuleBySlug, useLessons, useCourseWithModulesAndLessons } from '../../hooks/useCourseData';
import { useLessonMediaComplete } from '../../hooks/useLessonMedia';
import { getTenantId, getLessonResources, getCourseIdForModule } from '../../repos/contentRepo';
import { useAuth } from '../../contexts/AuthContext';
import { isPrivilegedContentRole } from '@/lib/courseAccess';
import { useCourseAccess } from '../../hooks/useCourseAccess';
import { getAdjacentLesson, getLessonPosition } from '@/lib/lessonNavigation';
import { useLessonProgress } from '../../hooks/useLessonProgress';

const durationToSeconds = (value, fallback = 0) => {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'number') return Number.isFinite(value) && value > 0 ? value * 60 : fallback;

  const normalized = String(value).trim();
  if (!normalized) return fallback;
  const parts = normalized.split(':').map((part) => Number(part));
  if (parts.some((part) => !Number.isFinite(part) || part < 0)) return fallback;
  if (parts.length === 3) return (parts[0] * 3600) + (parts[1] * 60) + parts[2];
  if (parts.length === 2) return (parts[0] * 60) + parts[1];
  if (parts.length === 1 && parts[0] > 0) return parts[0] * 60;
  return fallback;
};

const VideoPlayerInterface = () => {
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const [searchParams] = useSearchParams();
  const params = useParams();
  
  // Get lesson ID and context from route params (new) or query params (legacy)
  const lessonId = params.lessonId || searchParams?.get('id') || '1';
  const courseSlug = params.courseSlug;
  const moduleSlug = params.moduleSlug;
  
  // Use Supabase data if available
  const { lesson, loading: lessonLoading, error: lessonError } = useLesson(lessonId);
  
  // Use new media system
  const { 
    lesson: lessonWithMedia, 
    mainVideo, 
    supportingMedia, 
    mainVideoUrl, 
    loading: mediaLoading, 
    error: mediaError 
  } = useLessonMediaComplete(lessonId);
  
  // Get module data and lessons for navigation
  const [tenantId, setTenantId] = useState();
  const { course, modules: courseModules, loading: courseLoading, error: courseError } = useCourseWithModulesAndLessons(courseSlug);
  const { module, loading: moduleLoading, error: moduleError } = useModuleBySlug(tenantId, course?.id, moduleSlug);
  const { lessons: moduleLessons, loading: lessonsLoading, error: lessonsError } = useLessons(module?.id);

  // Debug: log hook states
  console.log('🔍 Hook States Debug:', {
    courseLoading,
    courseError,
    moduleLoading,
    moduleError,
    lessonsLoading,
    lessonsError,
    tenantId,
    courseId: course?.id,
    moduleId: module?.id
  });
  
  // Debug: list all lessons to see what's available
  const { lessons: allLessons, loading: allLessonsLoading } = useAllLessons();
  
  // Get tenant ID for module lookup
  useEffect(() => {
    if (courseSlug) {
      console.log('🔍 getTenantId called for courseSlug:', courseSlug);
      getTenantId().then(setTenantId);
    }
  }, [courseSlug]);
  
  // Debug: log lesson data
  console.log('🔍 VideoPlayer Debug:', {
    lessonId,
    lesson,
    lessonLoading,
    lessonError,
    courseSlug,
    moduleSlug,
    module,
    moduleLessons,
    allLessons: allLessons?.map(l => ({ id: l.id, title: l.title, module_id: l.module_id }))
  });

  // Debug: log breadcrumb data specifically
  console.log('🔍 Breadcrumb Debug:', {
    courseTitle: course?.title,
    moduleTitle: module?.title,
    lessonTitle: lesson?.title,
    courseSlug,
    moduleSlug,
    lessonId,
    tenantId,
    courseId: course?.id
  });

  // Debug: log parameter changes
  useEffect(() => {
    console.log('🔍 Parameters changed:', { courseSlug, moduleSlug, lessonId });
  }, [courseSlug, moduleSlug, lessonId]);

  // Debug: log useEffect dependencies
  useEffect(() => {
    console.log('🔍 useEffect dependencies:', { tenantId, courseId: course?.id, moduleSlug });
  }, [tenantId, course?.id, moduleSlug]);

  // Redirecionar para primeira lição quando em modo módulo (sem lessonId específico)
  useEffect(() => {
    if (moduleSlug && !params.lessonId && moduleLessons && moduleLessons.length > 0 && !allLessonsLoading) {
      const firstLesson = moduleLessons[0];
      console.log('🔄 Modo módulo detectado, redirecionando para primeira lição:', firstLesson.id);
      navigate(`/course/${courseSlug}/module/${moduleSlug}/lesson/${firstLesson.id}`, { replace: true });
    }
  }, [moduleSlug, params.lessonId, moduleLessons, allLessonsLoading, courseSlug, navigate]);

  /** undefined = resolvendo; null = sem curso; string = course id */
  const [resolvedCourseId, setResolvedCourseId] = useState(undefined);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [activeTab, setActiveTab] = useState('chapters');
  const [isChapterListCollapsed, setIsChapterListCollapsed] = useState(false);
  const [notes, setNotes] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [externalLinks, setExternalLinks] = useState([]);

  useEffect(() => {
    if (course?.id) {
      setResolvedCourseId(String(course.id));
      return;
    }
    if (!lesson?.module_id) {
      setResolvedCourseId(null);
      return;
    }
    let cancelled = false;
    setResolvedCourseId(undefined);
    getCourseIdForModule(lesson.module_id).then((cid) => {
      if (!cancelled) setResolvedCourseId(cid ? String(cid) : null);
    });
    return () => {
      cancelled = true;
    };
  }, [course?.id, lesson?.module_id]);

  const [playbackAllowed, setPlaybackAllowed] = useState(false);
  const [accessReady, setAccessReady] = useState(false);
  const [accessSessionError, setAccessSessionError] = useState(null);

  const courseIdForAccess =
    course?.id != null
      ? String(course.id)
      : resolvedCourseId && resolvedCourseId !== 'undefined'
        ? resolvedCourseId
        : undefined;

  const {
    isFullCourseEnrolled,
    isModuleAccessible,
    loading: courseAccessLoading,
  } = useCourseAccess(session?.systemUser?.id, courseIdForAccess);

  useEffect(() => {
    let cancelled = false;
    let sessionTimeoutId;

    function finish(allowed, sessionError = null) {
      if (cancelled) return;
      setAccessSessionError(sessionError);
      setPlaybackAllowed(allowed);
      setAccessReady(true);
    }

    if (!lesson) {
      setAccessReady(false);
      setPlaybackAllowed(false);
      setAccessSessionError(null);
      return undefined;
    }

    setAccessReady(false);
    setPlaybackAllowed(false);
    setAccessSessionError(null);

    const role = session?.userTenant?.role;

    if (isPrivilegedContentRole(role)) {
      finish(true);
      return undefined;
    }
    if (lesson.is_free) {
      finish(true);
      return undefined;
    }

    let cid;
    if (course?.id) {
      cid = String(course.id);
    } else if (resolvedCourseId === undefined) {
      return undefined;
    } else if (resolvedCourseId === null) {
      finish(false);
      return undefined;
    } else {
      cid = resolvedCourseId;
    }

    const systemUserId = session?.systemUser?.id;
    if (!systemUserId) {
      sessionTimeoutId = setTimeout(() => {
        finish(
          false,
          'Não foi possível verificar sua sessão. Faça login novamente para assistir à aula.'
        );
      }, 3000);
      return () => {
        cancelled = true;
        clearTimeout(sessionTimeoutId);
      };
    }

    if (courseAccessLoading) {
      return undefined;
    }

    const moduleId = lesson.module_id
      ? String(lesson.module_id)
      : module?.id
        ? String(module.id)
        : undefined;

    const allowed =
      isFullCourseEnrolled || (moduleId ? isModuleAccessible(moduleId) : false);

    finish(allowed);

    return () => {
      cancelled = true;
      clearTimeout(sessionTimeoutId);
    };
  }, [
    lesson,
    course?.id,
    resolvedCourseId,
    session?.systemUser?.id,
    session?.userTenant?.role,
    module?.id,
    courseAccessLoading,
    isFullCourseEnrolled,
    isModuleAccessible,
  ]);

  const blockedByEnrollment =
    lesson && accessReady && !playbackAllowed && !accessSessionError;
  const blockedBySession = lesson && accessReady && !!accessSessionError;
  const verifyingAccess =
    !!lesson &&
    !lessonLoading &&
    !accessReady &&
    !lesson.is_free &&
    !isPrivilegedContentRole(session?.userTenant?.role);

  const courseIdForProgress = course?.id
    ? String(course.id)
    : resolvedCourseId && resolvedCourseId !== 'undefined'
      ? resolvedCourseId
      : undefined;

  const {
    savedPercentage,
    lastPosition,
    isCompleted: lessonIsCompleted,
    saveProgress,
    persistProgress,
    markComplete,
  } = useLessonProgress({
    authUserId: user?.id,
    systemUserId: session?.systemUser?.id,
    lessonId: lesson?.id,
    moduleId: lesson?.module_id || module?.id,
    courseId: courseIdForProgress,
    tenantId,
    enabled: !!user?.id && !!lesson?.id && !!lesson?.module_id && !!courseIdForProgress && !!tenantId,
  });

  useEffect(() => {
    if (lastPosition > 0 && !lessonIsCompleted) {
      setCurrentTime(lastPosition);
    }
  }, [lastPosition, lessonIsCompleted, lessonId]);

  useEffect(() => {
    if (!courseSlug || !moduleSlug) {
      const defaultCourseSlug = 'formacao-lideres-mentores';
      const defaultModuleSlug = 'modulo-1-formacao-de-lideres-e-mentores';
      navigate(`/course/${defaultCourseSlug}/module/${defaultModuleSlug}/lesson/${lessonId}`);
    }
  }, [courseSlug, moduleSlug, lessonId, navigate]);

  const lessonPosition = useMemo(
    () => getLessonPosition(courseModules, lesson?.id),
    [courseModules, lesson?.id]
  );

  const previousTarget = useMemo(
    () => getAdjacentLesson(courseModules, lesson?.id, 'prev'),
    [courseModules, lesson?.id]
  );

  const nextTarget = useMemo(
    () => getAdjacentLesson(courseModules, lesson?.id, 'next'),
    [courseModules, lesson?.id]
  );

  const lessonDurationSeconds = useMemo(() => {
    return lesson?.duration_seconds ?? durationToSeconds(lesson?.duration);
  }, [lesson?.duration_seconds, lesson?.duration]);

  const effectiveDuration = duration > 0 ? duration : lessonDurationSeconds;

  const currentTimeRef = useRef(currentTime);
  const effectiveDurationRef = useRef(effectiveDuration);

  useEffect(() => {
    currentTimeRef.current = currentTime;
  }, [currentTime]);

  useEffect(() => {
    effectiveDurationRef.current = effectiveDuration;
  }, [effectiveDuration]);

  useEffect(() => {
    if (lessonDurationSeconds > 0 && duration === 0) {
      setDuration(lessonDurationSeconds);
    }
  }, [lessonDurationSeconds, lessonId, duration]);

  const liveProgress = effectiveDuration > 0 ? (currentTime / effectiveDuration) * 100 : 0;
  const progress = lessonIsCompleted
    ? 100
    : Math.max(savedPercentage, liveProgress);

  const flushProgressBeforeLeave = useCallback(async () => {
    const time = currentTimeRef.current;
    const total = effectiveDurationRef.current;
    if (total > 0) {
      await persistProgress(time, total);
      return;
    }

    if (!lessonIsCompleted && savedPercentage >= 90) {
      await markComplete();
    }
  }, [lessonIsCompleted, savedPercentage, persistProgress, markComplete]);

  const handleNavigatePrevious = useCallback(async () => {
    if (!previousTarget || !courseSlug) return;
    await flushProgressBeforeLeave();
    navigate(`/course/${courseSlug}/module/${previousTarget.moduleSlug}/lesson/${previousTarget.lessonId}`);
  }, [previousTarget, courseSlug, navigate, flushProgressBeforeLeave]);

  const handleNavigateNext = useCallback(async () => {
    if (!nextTarget || !courseSlug) return;
    await flushProgressBeforeLeave();
    navigate(`/course/${courseSlug}/module/${nextTarget.moduleSlug}/lesson/${nextTarget.lessonId}`);
  }, [nextTarget, courseSlug, navigate, flushProgressBeforeLeave]);

  const handleBackToCourse = useCallback(async () => {
    await flushProgressBeforeLeave();
    navigate(`/course/${courseSlug}`);
  }, [courseSlug, navigate, flushProgressBeforeLeave]);

  useEffect(() => {
    return () => {
      void flushProgressBeforeLeave();
    };
  }, [lessonId, flushProgressBeforeLeave]);

  // Lesson data will come from Supabase
  const mockLesson = {
    id: lessonId,
    title: lesson?.title || "Carregando...",
    description: lesson?.description || "Carregando descrição...",
    duration: lesson?.duration_seconds ?? durationToSeconds(lesson?.duration, 1800),
    moduleNumber: module?.id || 1,
    lessonNumber: lessonPosition?.current ?? 1,
    publishedAt: module?.updated_at || lesson?.created_at || new Date().toISOString(),
    views: 0,
    videoUrl: lesson?.video_url || "",
    objectives: [
      "Carregando objetivos...",
    ],
    tags: ["Carregando..."],
    instructor: {
      name: "Diane Andrade",
      title: "Instrutora",
      avatar: "/assets/images/diane-splash.png"
    }
  };

  const mockCourse = {
    title: module?.title || "Carregando...",
    totalLessons: lessonPosition?.total ?? moduleLessons?.length ?? 0
  };

  const mockChapters = [];

  const mockRelatedVideos = [];

  const mockNotes = [];

  useEffect(() => {
    setNotes(mockNotes);
  }, []);

  // Carregar recursos da lição (anexos e links externos)
  useEffect(() => {
    const loadLessonResources = async () => {
      try {
        const resources = await getLessonResources(lessonId);
        setAttachments(resources.attachments);
        setExternalLinks(resources.externalLinks);
      } catch (error) {
        console.error('Erro ao carregar recursos da lição:', error);
        setAttachments([]);
        setExternalLinks([]);
      }
    };

    if (lessonId) {
      loadLessonResources();
    }
  }, [lessonId]);

  const handleTimeUpdate = useCallback((current, total) => {
    const resolvedTotal = total > 0 ? total : lessonDurationSeconds;
    setCurrentTime(current);
    if (resolvedTotal > 0) {
      setDuration(resolvedTotal);
      saveProgress(current, resolvedTotal);
    }
  }, [lessonDurationSeconds, saveProgress]);

  const handleVideoEnded = useCallback(() => {
    void markComplete();
  }, [markComplete]);

  const handleToggleBookmark = () => {
    setIsBookmarked(!isBookmarked);
  };

  const handleShareVideo = () => {
    if (navigator.share) {
      navigator.share({
        title: lesson?.title || 'Lição',
        text: lesson?.description || 'Confira esta lição',
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      // Show toast: "Link copiado para a área de transferência"
    }
  };

  const handleChapterClick = (timestamp) => {
    // Seek to specific timestamp in video
    console.log('Seeking to timestamp:', timestamp);
  };

  const handleVideoSelect = (videoId) => {
    // Navigate to selected video
    console.log('Selected video:', videoId);
  };

  const handleAddNote = (note) => {
    setNotes(prev => [...prev, { ...note, id: Date.now() }]);
  };

  const handleDeleteNote = (noteId) => {
    setNotes(prev => prev.filter(note => note.id !== noteId));
  };

  const handleEditNote = (noteId, updatedNote) => {
    setNotes(prev => prev.map(note => 
      note.id === noteId ? { ...note, ...updatedNote } : note
    ));
  };

  const handleSeekToNote = (timestamp) => {
    console.log('Seeking to note timestamp:', timestamp);
  };

  const tabs = [
    { id: 'chapters', label: 'Capítulos', icon: 'List' },
    { id: 'related', label: 'Relacionados', icon: 'PlaySquare' },
    { id: 'notes', label: 'Notas', icon: 'StickyNote' }
  ];

  // Construir breadcrumb com fallbacks inteligentes
  const getBreadcrumbLabel = (data, fallback, loading) => {
    if (loading) return 'Carregando...';
    if (data) return data;
    return fallback;
  };

  const breadcrumbItems = [
    { label: 'Início', path: '/dashboard-home' },
    { label: 'Cursos', path: '/courses' },
    { 
      label: getBreadcrumbLabel(course?.title, 'Curso', courseLoading), 
      path: `/course/${courseSlug}` 
    },
    { 
      label: getBreadcrumbLabel(module?.title, 'Módulo', moduleLoading), 
      path: `/course/${courseSlug}/module/${moduleSlug}` 
    },
    { 
      label: getBreadcrumbLabel(lesson?.title, 'Lição', lessonLoading), 
      path: `/course/${courseSlug}/module/${moduleSlug}/lesson/${lessonId}`, 
      isActive: true 
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Back Navigation */}
          <div className="flex items-center space-x-4 mb-6 min-w-0">
            <Button
              variant="ghost"
              onClick={handleBackToCourse}
              iconName="ArrowLeft"
              iconPosition="left"
              iconSize={20}
            >
              Voltar ao curso
            </Button>
            <div className="flex-1 min-w-0">
              <Breadcrumb customItems={breadcrumbItems} />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {verifyingAccess && (
                <div className="bg-muted rounded-lg p-8 text-center">
                  <div className="w-16 h-16 bg-muted-foreground/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                    <Icon name="Loader2" size={32} className="text-muted-foreground animate-spin" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Verificando acesso...</h3>
                  <p className="text-muted-foreground mb-4">Aguarde um momento.</p>
                </div>
              )}

              {blockedBySession && (
                <div className="bg-muted rounded-lg p-8 text-center">
                  <div className="w-16 h-16 bg-muted-foreground/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="AlertCircle" size={32} className="text-muted-foreground" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Sessão não verificada</h3>
                  <p className="text-muted-foreground mb-4">{accessSessionError}</p>
                  <Button variant="default" onClick={() => navigate('/login-screen')}>
                    Fazer login
                  </Button>
                </div>
              )}

              {blockedByEnrollment && (
                <div className="bg-muted rounded-lg p-8 text-center">
                  <div className="w-16 h-16 bg-muted-foreground/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Lock" size={32} className="text-muted-foreground" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Acesso não liberado</h3>
                  <p className="text-muted-foreground mb-4">
                    Você não tem acesso a esta lição. Adquira o curso completo ou o módulo correspondente para liberar o conteúdo.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() =>
                      navigate(courseSlug ? `/course/${courseSlug}` : '/courses')
                    }
                  >
                    {courseSlug ? 'Voltar ao curso' : 'Catálogo de cursos'}
                  </Button>
                </div>
              )}

              {lessonLoading && (
                <div className="bg-muted rounded-lg p-8 text-center">
                  <div className="w-16 h-16 bg-muted-foreground/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                    <Icon name="Loader2" size={32} className="text-muted-foreground animate-spin" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Carregando lição...</h3>
                  <p className="text-muted-foreground mb-4">
                    Aguarde enquanto carregamos o conteúdo da lição.
                  </p>
                </div>
              )}

              {accessReady && playbackAllowed && !lessonLoading && lesson && (
                <>
                  <MediaPlayer
                    mediaMapping={mainVideo}
                    fallbackUrl={lesson?.video_url || mockLesson?.videoUrl}
                    onTimeUpdate={handleTimeUpdate}
                    onComplete={handleVideoEnded}
                    currentTime={currentTime}
                    duration={duration}
                    className="w-full aspect-video bg-card rounded-lg overflow-hidden"
                  />

                  <VideoInfo
                    lesson={lesson ? {
                      ...lesson,
                      title: lesson.title,
                      description: lesson.description || '',
                      duration: lesson.duration_seconds ?? durationToSeconds(lesson.duration, 1800),
                      views: 0
                    } : mockLesson}
                    course={mockCourse}
                    progress={progress}
                    onToggleBookmark={handleToggleBookmark}
                    onShareVideo={handleShareVideo}
                    isBookmarked={isBookmarked}
                    lessonPosition={lessonPosition}
                    onNavigatePrevious={handleNavigatePrevious}
                    onNavigateNext={handleNavigateNext}
                    hasPrevious={!!previousTarget}
                    hasNext={!!nextTarget}
                  />
                </>
              )}

              {/* Mobile Tabs */}
              <div className="lg:hidden">
                <div className="flex space-x-1 bg-muted rounded-lg p-1 mb-4">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex-1 flex items-center justify-center space-x-2 py-2 px-3 rounded-md text-sm font-medium transition-micro ${
                        activeTab === tab?.id
                          ? 'bg-card text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Icon name={tab?.icon} size={16} />
                      <span>{tab?.label}</span>
                    </button>
                  ))}
                </div>

                {/* Mobile Tab Content */}
                {activeTab === 'chapters' && (
                  <ChapterList
                    chapters={mockChapters}
                    currentTime={currentTime}
                    onChapterClick={handleChapterClick}
                    isCollapsed={isChapterListCollapsed}
                    onToggleCollapse={() => setIsChapterListCollapsed(!isChapterListCollapsed)}
                    attachments={attachments}
                  />
                )}

                {activeTab === 'related' && (
                  <RelatedVideos
                    videos={mockRelatedVideos}
                    currentVideoId={lessonId}
                    onVideoSelect={handleVideoSelect}
                    externalLinks={externalLinks}
                  />
                )}

                {activeTab === 'notes' && (
                  <VideoNotes
                    notes={notes}
                    currentTime={currentTime}
                    onAddNote={handleAddNote}
                    onDeleteNote={handleDeleteNote}
                    onEditNote={handleEditNote}
                    onSeekToNote={handleSeekToNote}
                  />
                )}
              </div>
            </div>

            {/* Sidebar - Desktop Only */}
            <div className="hidden lg:block space-y-6">
              {/* Chapters */}
              <ChapterList
                chapters={mockChapters}
                currentTime={currentTime}
                onChapterClick={handleChapterClick}
                isCollapsed={isChapterListCollapsed}
                onToggleCollapse={() => setIsChapterListCollapsed(!isChapterListCollapsed)}
                attachments={attachments}
              />

              {/* Related Videos */}
              <RelatedVideos
                videos={mockRelatedVideos}
                currentVideoId={lessonId}
                onVideoSelect={handleVideoSelect}
                externalLinks={externalLinks}
              />

              {/* Notes */}
              <VideoNotes
                notes={notes}
                currentTime={currentTime}
                onAddNote={handleAddNote}
                onDeleteNote={handleDeleteNote}
                onEditNote={handleEditNote}
                onSeekToNote={handleSeekToNote}
              />
            </div>
          </div>
        </div>
      </main>
      <FloatingAIChat />
    </div>
  );
};

export default VideoPlayerInterface;
